/* eslint-disable no-restricted-imports */
import { B2B } from '@/components/blocks/B2B';
import { LoginRegistrationB2BForm } from '@/components/content/Login/parts/LoginRegistrationB2BForm';
import {
	createAccountContainerSX,
	loginContainerHomeSX,
} from '@/components/content/Login/styles/container';
import {
	LOGIN_STEPS,
	OTP_METHODS,
	UserLogon,
	personMutatorKeyMatcher,
	useLoginLess,
} from '@/data/Content/Login';
import { CreateAccountLoginForm } from '@/components/content/Login/parts/CreateAccountLogin';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { ID } from '@/data/types/Basic';
import { Box, Divider, Grid, Paper } from '@mui/material';
import { FC, useContext, useEffect } from 'react';
import { useSWRConfig } from 'swr';
import { useIsMobile } from '@/data/hooks/useIsMobile';
import { useRecaptchaV3Setup } from '@/data/Content/Recaptcha';
import { useApplyBankPromotionInAuth } from '@/data/Content/BanksPromotion';
import { useNotifications } from '@/data/Content/Notifications';
import { useSuccessLoginNotify } from '@/utils/useSuccessLoginNotify';
import { usePasswordExpiredRedirect } from '@/utils/usePasswordExpiredRedirect';
import { useReplaceAuthRoute } from 'integration/hooks/useReplaceAuthRoute';
import { SingleLoginLessForm } from '@/components/blocks/SingleLoginLessForm';
import { SingleLoginLessTitle } from '@/components/blocks/SingleLoginLessTitle';
import { LoginOriginProvider } from '@/data/context/loginOrigin';
import { EventsContext } from '@/data/context/events';
import { useSettings } from '@/data/Settings';

const LoginContent: FC<{ id: ID }> = () => {
	const isMobile = useIsMobile();
	const router = useNextRouter();
	const { mutate } = useSWRConfig();
	const { recaptchaEnabled, recaptchaExecute, recaptchaLoading } = useRecaptchaV3Setup();
	const { onApplyBankPromotionInAuth } = useApplyBankPromotionInAuth();
	const { notifyError } = useNotifications();
	const { showSuccessAuthUser } = useSuccessLoginNotify();
	const { onLoginOpen } = useContext(EventsContext);
	const { settings } = useSettings();

	useEffect(() => {
		onLoginOpen({ gtm: { flow: 'login', settings, login_origin: 'login_page' } });
	}, [onLoginOpen, settings]);

	const contentSpacingLogin = {
		xs: 2,
		sm: 2,
		md: 6,
		lg: 32.5,
	};

	const { onReplaceAuthRoute } = useReplaceAuthRoute({ shouldCallToModal: false });
	const {
		stateLogin,
		baseSettings,
		singleSettingsLoading,
		nextRouteLoading,
		onChangeLogonId,
		onBlurLogonId,
		onSubmitInitialLogonId,
		onChangeOtpMethod,
		onSubmitOtpMethod,
		onChangeOtp,
		onToggleLoginPassword,
		onChangePassword,
		onSubmitLogin,
		onSubmitGoogleLogin,
		onToggleSelectOtpMethod,
		onToggleBackStep,
		loginBase: { loginSubmit, setPasswordExpired, passwordExpired },
	} = useLoginLess();
	usePasswordExpiredRedirect(passwordExpired, setPasswordExpired);

	const handleAfterLogin = async (resp: any) => {
		if ('userId' in (resp ?? {})) {
			const bankPromotion = await onApplyBankPromotionInAuth();

			if (bankPromotion.redirect) {
				await router.push(bankPromotion.redirect);
			} else {
				await onReplaceAuthRoute();
			}

			if (!bankPromotion.success && bankPromotion.message) {
				notifyError(bankPromotion.message);
			}

			await mutate(personMutatorKeyMatcher(''), undefined);
			await showSuccessAuthUser();
		}
	};

	const handleLogin = async (props?: UserLogon) => {
		let x_recaptchaToken: string | undefined = undefined;

		if (recaptchaEnabled) {
			x_recaptchaToken = await recaptchaExecute('login');
		}

		let resp: any = {};

		if (props && !('nativeEvent' in props)) {
			resp = await loginSubmit({ ...props, x_recaptchaToken });
		} else {
			resp = await onSubmitLogin({ x_recaptchaToken });
		}

		await handleAfterLogin(resp);
	};

	const handleLoginOtpMethod = async (props?: UserLogon) => {
		let x_recaptchaToken: string | undefined = undefined;

		if (recaptchaEnabled && stateLogin.otpMethod === OTP_METHODS.PASSWORD) {
			x_recaptchaToken = await recaptchaExecute('login');
		}

		let resp: any = {};

		if (props && !('nativeEvent' in props)) {
			resp = await loginSubmit({ ...props, x_recaptchaToken });
		} else {
			resp = await onSubmitOtpMethod({ x_recaptchaToken });
		}

		await handleAfterLogin(resp);
	};

	const handleGoogleLogin = async (idToken: string) => {
		let x_recaptchaToken: string | undefined = undefined;

		if (recaptchaEnabled) {
			x_recaptchaToken = await recaptchaExecute('login');
		}

		const resp = await onSubmitGoogleLogin(idToken, { x_recaptchaToken });

		await handleAfterLogin(resp);
	};

	return (
		<Grid container spacing={contentSpacingLogin} sx={{ paddingTop: { md: '0px', xs: '0px' } }}>
			<Grid item xs={12} sm={8} md={6} m="auto">
				<Paper
					sx={loginContainerHomeSX({
						isMobile,
						marginBottom: stateLogin.step === LOGIN_STEPS.INITIAL_LOGON_ID ? undefined : '40px',
					})}
				>
					{passwordExpired !== null ? null : (
						<Box>
							<SingleLoginLessTitle
								step={stateLogin.step}
								otpMethod={stateLogin.otpMethod}
								maskedPhone={stateLogin.maskedPhone}
								maskedEmail={stateLogin.maskedEmail}
								showPreviousMethodHint={stateLogin.showPreviousMethodHint}
								onToggleBackStep={onToggleBackStep}
							/>
							<SingleLoginLessForm
								stateLogin={stateLogin}
								baseSettings={baseSettings}
								disabled={recaptchaLoading || singleSettingsLoading || nextRouteLoading}
								onChangeLogonId={onChangeLogonId}
								onBlurLogonId={onBlurLogonId}
								onSubmitInitialLogonId={onSubmitInitialLogonId}
								onChangeOtpMethod={onChangeOtpMethod}
								onSubmitOtpMethod={handleLoginOtpMethod}
								onChangeOtp={onChangeOtp}
								onChangePassword={onChangePassword}
								onSubmitLogin={() => handleLogin()}
								onToggleLoginPassword={onToggleLoginPassword}
								onToggleSelectOtpMethod={onToggleSelectOtpMethod}
								onGoogleLogin={handleGoogleLogin}
							/>
						</Box>
					)}
				</Paper>
				{stateLogin.step === LOGIN_STEPS.INITIAL_LOGON_ID ? (
					<Paper sx={createAccountContainerSX({ isMobile })}>
						{isMobile ? <Divider sx={{ marginBottom: '15px' }} /> : null}
						<CreateAccountLoginForm onSubmit={handleLogin} />
					</Paper>
				) : null}
			</Grid>
			<B2B>
				<Grid item xs={12} sm={8} md={6}>
					<LoginRegistrationB2BForm />
				</Grid>
			</B2B>
		</Grid>
	);
};

export const Login: FC<{ id: ID }> = (props) => (
	<LoginOriginProvider origin="login_page">
		<LoginContent {...props} />
	</LoginOriginProvider>
);
